import React from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { MatchSuggestion } from 'shared';

interface MatchSuggestionsProps {
  suggestions: MatchSuggestion[];
  loading: boolean;
  onSelect: (suggestion: MatchSuggestion) => void;
}

export const MatchSuggestions: React.FC<MatchSuggestionsProps> = ({
  suggestions,
  loading,
  onSelect,
}) => {
  const getConfidenceSeverity = (score: number): "success" | "warning" | "secondary" => {
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    return 'secondary';
  };

  if (loading) {
    return (
      <div data-component="bank-import-match-suggestions" style={{ textAlign: 'center', padding: '1rem' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
        <p style={{ color: 'var(--text-color-secondary)', marginTop: '0.5rem' }}>Matching...</p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div data-component="bank-import-match-suggestions" style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-color-secondary)' }}>
        No matching members found
      </div>
    );
  }

  return (
    <div data-component="bank-import-match-suggestions" style={{ display: 'block' }}>
      <h4 style={{ margin: '0 0 0.5rem 0' }}>Suggested Matches</h4>
      {suggestions.map((suggestion, idx) => (
        <div
          key={idx}
          onClick={() => onSelect(suggestion)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--surface-100)',
            borderRadius: '6px',
            padding: '0.75rem',
            marginBottom: '0.5rem',
            cursor: 'pointer',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
              <Tag
                value={`${suggestion.score}%`}
                severity={getConfidenceSeverity(suggestion.score)}
                style={{ fontSize: '0.75rem' }}
              />
              <span style={{ fontWeight: 600 }}>
                {suggestion.firstName} {suggestion.lastName}
              </span>
              <span style={{ color: 'var(--text-color-secondary)', fontSize: '0.875rem' }}>({suggestion.memberNumber})</span>
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-color-secondary)' }}>
              {suggestion.reasons.join(' | ')}
            </div>
            {suggestion.matchedInvoiceNumber && (
              <div style={{ fontSize: '0.875rem', color: 'var(--primary-color)', marginTop: '0.25rem' }}>
                <i className="pi pi-file" style={{ marginRight: '0.25rem' }} />
                Invoice: {suggestion.matchedInvoiceNumber}
                {suggestion.matchedInvoiceAmount !== undefined && (
                  <span> ({new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(suggestion.matchedInvoiceAmount)})</span>
                )}
              </div>
            )}
          </div>
          <Button
            icon="pi pi-arrow-right"
            className="p-button-rounded p-button-text p-button-sm"
            tooltip="Use this match"
            tooltipOptions={{ position: 'left' }}
          />
        </div>
      ))}
    </div>
  );
};
